import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableCellNode } from '@lexical/table';
import {
  $getSelection,
  $isParagraphNode,
  $nodesOfType,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useEffect, useRef } from 'react';
import {
  $containingSuggestionNode,
  $focusSuggestion,
  $getSuggestionState,
  $setMarkersHidden,
  $setSuggestionAbsolute,
  SuggestionNode,
} from '../nodes/SuggestionNode';

type Props = {
  showMarkers?: boolean;
};

export function SuggestionPlugin({ showMarkers = true }: Props) {
  const [editor] = useLexicalComposerContext();
  useSuggestionCellStyle(editor);
  useSuggestionFocusStyle(editor, showMarkers);
  useMarkersVisibility(editor, showMarkers);
  return null;
}

function useSuggestionCellStyle(editor: LexicalEditor) {
  useEffect(() => {
    return editor.registerNodeTransform(SuggestionNode, node => {
      const parent = node.getParent();
      const grandParent = parent?.getParent();

      const parentIsParagraph = $isParagraphNode(parent);
      const grandParentIsTableCell = $isTableCellNode(grandParent);
      const isInTableCell = parentIsParagraph && grandParentIsTableCell;

      const paragraphChildren = parent?.getChildren() || [];
      const isTableCellsOnlyContent = paragraphChildren.every(
        child => child === node || child.getTextContent().trim().length === 0,
      );

      const suggestionIsOnlyCellContent = isInTableCell && isTableCellsOnlyContent;

      const currentState = $getSuggestionState(node);

      if (currentState.isAbsolute !== suggestionIsOnlyCellContent) {
        $setSuggestionAbsolute(node, suggestionIsOnlyCellContent);
      }
    });
  }, [editor]);
}

function useSuggestionFocusStyle(editor: LexicalEditor, showMarkers: boolean) {
  const showMarkersRef = useRef(showMarkers);

  useEffect(() => {
    showMarkersRef.current = showMarkers;
  }, [showMarkers]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        if (!showMarkersRef.current) return false;

        const selection = $getSelection();
        selection?.getNodes().forEach(node => {
          const suggestionNode = $containingSuggestionNode(node);
          if (suggestionNode) {
            $focusSuggestion(suggestionNode);
          }
        });
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);
}

function useMarkersVisibility(editor: LexicalEditor, showMarkers: boolean) {
  const prevShowMarkersRef = useRef(showMarkers);

  useEffect(() => {
    if (prevShowMarkersRef.current !== showMarkers) {
      prevShowMarkersRef.current = showMarkers;

      // Prevent the caret being scrolled into view after toggling markers
      editor.update(() => {
        $setSelection(null);
      });

      editor.update(() => {
        $nodesOfType(SuggestionNode).forEach(node => {
          $setMarkersHidden(node, !showMarkers);
        });
      });
    }
  }, [editor, showMarkers]);
}
